import { useContext, useEffect, useRef } from 'react';
import { FitViewOptions, ViewportHelperFunctionOptions } from 'reactflow';

import { InteractionDagNode } from '../dag/internalDag';
import { DbtDagNode } from '../dbt-dag';
import { DagContext } from '../providers/DagProvider';

export type OnNodeClick<TDbtDagNode extends DbtDagNode> = (
  event: React.MouseEvent<Element, MouseEvent>,
  node: InteractionDagNode<TDbtDagNode>,
) => void;

interface EventQueueProps<TDbtDagNode extends DbtDagNode> {
  resetDagLayoutAndZoom: () => void;
  onNodeClick: OnNodeClick<TDbtDagNode>;
  onNodeDoubleClick: OnNodeClick<TDbtDagNode>;
  onNodeRightClick: OnNodeClick<TDbtDagNode>;
  updateHighlightedNodes: (selected: string[] | undefined) => void;
  hideContextMenu: () => void;
  zoomIn: (options?: ViewportHelperFunctionOptions) => void;
  zoomOut: (options?: ViewportHelperFunctionOptions) => void;
  fitView: (options?: FitViewOptions) => void;
}

/**
 * Handles events from the DbtDagContext that may be fired from outside
 * of the Dag component.
 */
export const useEventQueue = <TDbtDagNode extends DbtDagNode>({
  resetDagLayoutAndZoom,
  onNodeClick,
  onNodeDoubleClick,
  onNodeRightClick,
  updateHighlightedNodes,
  hideContextMenu,
  zoomIn,
  zoomOut,
  fitView,
}: EventQueueProps<TDbtDagNode>) => {
  const { eventQueue, clearEventQueue } = useContext(DagContext);

  const resetDagLayoutAndZoomRef = useRef(resetDagLayoutAndZoom);
  resetDagLayoutAndZoomRef.current = resetDagLayoutAndZoom;

  const onNodeClickRef = useRef(onNodeClick);
  onNodeClickRef.current = onNodeClick;

  const onNodeDoubleClickRef = useRef(onNodeDoubleClick);
  onNodeDoubleClickRef.current = onNodeDoubleClick;

  const onNodeRightClickRef = useRef(onNodeRightClick);
  onNodeRightClickRef.current = onNodeRightClick;

  const updateHighlightedNodeRef = useRef(updateHighlightedNodes);
  updateHighlightedNodeRef.current = updateHighlightedNodes;

  const hideContextMenuRef = useRef(hideContextMenu);
  hideContextMenuRef.current = hideContextMenu;

  const zoomInRef = useRef(zoomIn);
  zoomInRef.current = zoomIn;

  const zoomOutRef = useRef(zoomOut);
  zoomOutRef.current = zoomOut;

  const fitViewRef = useRef(fitView);
  fitViewRef.current = fitView;

  useEffect(() => {
    if (eventQueue.length === 0) return;

    let shouldResetZoom = false;
    let clickedNode:
      | {
          event: React.MouseEvent<Element, MouseEvent>;
          node: InteractionDagNode<TDbtDagNode>;
        }
      | undefined;
    let doubleClickedNode:
      | {
          event: React.MouseEvent<Element, MouseEvent>;
          node: InteractionDagNode<TDbtDagNode>;
        }
      | undefined;
    let rightClickedNode:
      | {
          event: React.MouseEvent<Element, MouseEvent>;
          node: InteractionDagNode<TDbtDagNode>;
        }
      | undefined;
    let highlightNodeIds: string[] | undefined;
    let shouldHideContextMenu: boolean = true;

    let shouldZoomIn: [boolean, ViewportHelperFunctionOptions?] = [false, undefined];
    let shouldZoomOut: [boolean, ViewportHelperFunctionOptions?] = [false, undefined];
    let shouldFitView: [boolean, FitViewOptions?] = [false, undefined];

    eventQueue.forEach((event) => {
      if (event.type === 'resetDagLayoutAndZoom') {
        shouldResetZoom = true;
      } else if (event.type === 'nodeClick') {
        clickedNode = event;
        highlightNodeIds = [event.node.id];
      } else if (event.type === 'nodeDoubleClick') {
        doubleClickedNode = event;
      } else if (event.type === 'nodeRightClick') {
        rightClickedNode = event;
        highlightNodeIds = [event.node.id];
        shouldHideContextMenu = false;
      } else if (event.type === 'updateHighlightedNodes') {
        highlightNodeIds = event.nodeIds;
      } else if (event.type === 'zoomIn') {
        shouldZoomIn = [true, event.options];
      } else if (event.type === 'zoomOut') {
        shouldZoomOut = [true, event.options];
      } else if (event.type === 'fitView') {
        shouldFitView = [true, event.options];
      }
    });
    if (shouldResetZoom) {
      resetDagLayoutAndZoomRef.current();
    }
    if (shouldZoomIn[0]) {
      zoomInRef.current(shouldZoomIn[1]);
    }
    if (shouldZoomOut[0]) {
      zoomOutRef.current(shouldZoomOut[1]);
    }
    if (shouldFitView[0]) {
      fitViewRef.current(shouldFitView[1]);
    }
    /**
     * We'll only process a single, double, or right click in one event loop.
     */
    if (doubleClickedNode) {
      onNodeDoubleClickRef.current(doubleClickedNode.event, doubleClickedNode.node);
    } else if (clickedNode) {
      onNodeClickRef.current(clickedNode.event, clickedNode.node);
    } else if (rightClickedNode) {
      onNodeRightClickRef.current(rightClickedNode.event, rightClickedNode.node);
    }

    if (highlightNodeIds) {
      updateHighlightedNodeRef.current(highlightNodeIds);
    }
    if (shouldHideContextMenu) {
      hideContextMenuRef.current();
    }

    clearEventQueue();
  }, [clearEventQueue, eventQueue]);
};
